import { CloseOnSendDto } from './caseClosing';
import { ContactMinDto } from './contacts';
import { ContractMinDetailsDto } from './contracts';
import { DocumentTemplateMinDto } from './document';
import { EmpMinDto } from './employees';
import { InvoiceMatterCalculationLog } from './invoice-details';
import { MatterBillingSummaryDto } from './matters';
import { OfficeMin } from './offices';
import {
    AuditDates,
    BillingInvoiceInfo,
    MinDto,
    NamedEntityMinDto,
    NamedEntityMinWithEmailDto,
    NamedEntityMinWithMainEmailDto,
    NumericIdPrimKeyDto,
    SimpleCurrencyDto,
} from './other';
import { DebtReminderType } from './tenants';

export enum InvoicePaymentTypeCode {
    BANK_TRANSFER = 'BANK_TRANSFER',
    DIRECT_PAYMENT = 'DIRECT_PAYMENT',
    PREPAID_INVOICE = 'PREPAID_INVOICE',
}

export enum EditIntentTypeCode {
    MOVE_TO_ANOTHER_CASE = 'MOVE_TO_ANOTHER_CASE',
    MARK_AS_NONBILLABLE = 'MARK_AS_NONBILLABLE',
    POSTPONE = 'POSTPONE',
    REMOVE_VIA_CORRECTION = 'REMOVE_VIA_CORRECTION',
}

export enum InvoiceStatusType {
    DRAFT = 'DRAFT',
    REVIEWED = 'REVIEWED',
    APPROVED = 'APPROVED',
    FINALIZED = 'FINALIZED',
    SENT = 'SENT',
    PAID = 'PAID',
    CREDITED = 'CREDITED',
    POSTPONED = 'POSTPONED',
}

export enum InvoiceType {
    CREDIT = 'CREDIT',
    DEBIT = 'DEBIT',
    CORRECTION = 'CORRECTION',
    PREPAYMENT = 'PREPAYMENT',
}

export enum InvoiceTransactionActionType {
    APPROVED = 'APPROVED',
    REJECTED = 'REJECTED',
    POSTPONED = 'POSTPONED',
}

export enum InvoiceEmailTemplateType {
    DRAFT_INVOICE = 'DRAFT_INVOICE',
    INVOICE = 'INVOICE',
    BULK_INVOICES = 'BULK_INVOICES',
}

export enum InvoiceDebtReminderScopeType {
    SINGLE_INVOICE = 'SINGLE_INVOICE',
    ALL_COMPANY_INVOICES = 'ALL_COMPANY_INVOICES',
    ALL_INVOICES = 'ALL_INVOICES',
}

export enum InvoiceDebtReminderType {
    TYPE1 = 'TYPE1',
    TYPE2 = 'TYPE2',
    TYPE3 = 'TYPE3',
    TYPE4 = 'TYPE4',
    OVERVIEW = 'OVERVIEW',
}

export enum InvoiceDebtReminderEmailTemplateType {
    DEBT_REMINDER_SINGLE_INVOICE_TYPE1 = 'DEBT_REMINDER_SINGLE_INVOICE_TYPE1',
    DEBT_REMINDER_SINGLE_INVOICE_TYPE2 = 'DEBT_REMINDER_SINGLE_INVOICE_TYPE2',
    DEBT_REMINDER_SINGLE_INVOICE_TYPE3 = 'DEBT_REMINDER_SINGLE_INVOICE_TYPE3',
    DEBT_REMINDER_SINGLE_INVOICE_TYPE4 = 'DEBT_REMINDER_SINGLE_INVOICE_TYPE4',
    DEBT_REMINDER_SINGLE_INVOICE_OVERVIEW = 'DEBT_REMINDER_SINGLE_INVOICE_OVERVIEW',
    DEBT_REMINDER_ALL_COMPANY_INVOICES_TYPE1 = 'DEBT_REMINDER_ALL_COMPANY_INVOICES_TYPE1',
    DEBT_REMINDER_ALL_COMPANY_INVOICES_TYPE2 = 'DEBT_REMINDER_ALL_COMPANY_INVOICES_TYPE2',
    DEBT_REMINDER_ALL_COMPANY_INVOICES_TYPE3 = 'DEBT_REMINDER_ALL_COMPANY_INVOICES_TYPE3',
    DEBT_REMINDER_ALL_COMPANY_INVOICES_TYPE4 = 'DEBT_REMINDER_ALL_COMPANY_INVOICES_TYPE4',
    DEBT_REMINDER_ALL_COMPANY_INVOICES_OVERVIEW = 'DEBT_REMINDER_ALL_COMPANY_INVOICES_OVERVIEW',
    DEBT_REMINDER_ALL_INVOICES_TYPE1 = 'DEBT_REMINDER_ALL_INVOICES_TYPE1',
    DEBT_REMINDER_ALL_INVOICES_TYPE2 = 'DEBT_REMINDER_ALL_INVOICES_TYPE2',
    DEBT_REMINDER_ALL_INVOICES_TYPE3 = 'DEBT_REMINDER_ALL_INVOICES_TYPE3',
    DEBT_REMINDER_ALL_INVOICES_TYPE4 = 'DEBT_REMINDER_ALL_INVOICES_TYPE4',
    DEBT_REMINDER_ALL_INVOICES_OVERVIEW = 'DEBT_REMINDER_ALL_INVOICES_OVERVIEW',
}
export interface InvoiceStatusUpdateDto {
    id: number;
    invoiceStatusTypeCode?: InvoiceStatusType | null;
    transitionComment?: string;
    transitionActionTypeCode?: InvoiceTransactionActionType | null;
}

export interface MatterAmountDto {
    matterId: number;
    amount: string;
}

export interface ErroredMatterDto {
    matterId: number;
    matterName: string | null;
    code: string | null;
    message: string | null;
}

export interface CreatedDraftsDto {
    createdInvoices: number[];
    erroredMatters: ErroredMatterDto[];
}

export interface CreateDraftInvoicesDto {
    matterIds: number[];
    startDate: string | null;
    endDate: string | null;
}

export interface InvoiceDataListItem {
    id: number;
    paymentCurrencyTypeCode: string;
    clientParty: ContactMinDto;
    invoiceMattersClientParties: ContactMinDto[];
    invoiceTypeCode: string;
    invoiceNumber: string;
    invoiceDate: string;
    dueDate: string;
    statusChangeDate: string;
    invoiceStatusTypeCode: string;
    invoiceTotals?: InvoiceListItemTotalsDto;
    invoiceDeltaTotals?: InvoiceListItemTotalsDto;
    mainMatter: InvoiceMatterItemDto;
    matters: InvoiceMatterItemDto[];
    billedTimeInHours: number;
    paidOnDate?: string;
    creditInvoices: InvoiceMinDto[];
    finalPayableAmountInPaymentCurrency: string;
    outstandingBalanceInPaymentCurrency: string;
    outstandingBalanceInBaseCurrency: string;
    totalAmountInPaymentCurrency: string;
    isSplit?: boolean;
    paymentShareRate?: number | null;
    /** The numbers of the other invoices of this shared-payment group; empty for a regular invoice. */
    sharedPaymentInvoiceNumbers?: string[];
    relationshipPartners: MinDto[];
    supervisingPartners: MinDto[];
    responsibleLawyers: MinDto[];
    invoicedByOffice: OfficeMin;
    invoicedByOfficeCompany: string;
    billingInstructions?: string;
    primaryRecipientParty?: ContactMinDto;
    debtNotes?: string;
    nextReminderDate?: string;
    invoiceReminders?: InvoiceReminderDto[];
    primaryRecipientAdditionalEmails?: string;
    secondaryRecipientMainEmails: string[];
    secondaryRecipientAdditionalEmails?: string;
    invoiceArchiveEmails?: string;
    isFixedFeeMattersOnly: boolean;
    isSpecAvailable: boolean;
    isSpecIncludedInInvoice: boolean;
    isSpecExcludedFromEmail: boolean;
    invoicePdfLanguage?: string;
    latestSentInvoiceId?: number;
    externalStatusTypeCode?: string;
    externalActionTypeCode?: string;
    externalInvoiceNumber?: string;
    externalEventId?: string;
    externalInvoiceState?: string;
    externalInvoiceStateReason?: string;
}

export interface InvoiceExportListItemDto {
    id: number;
    accountingRevenueTypeCode: string;
    issuedByOffice: string;
    issuedByCompany: string;
    payer: InvoicePayer;
    clientParties: MinDto[];
    invoiceDate: string;
    dueDate: string;
    invoiceTypeCode: string;
    invoiceStatusTypeCode: string;
    invoiceNumber: string;
    creditedByInvoices: RelatedInvoiceDto[];
    debitInvoice?: RelatedInvoiceDto;
    invoiceCurrencyTypeCode: string;
    paymentCurrencyTypeCode: string;
    baseCurrencyTypeCode: string;
    billingInstructions: string;
    tcFromDate: string;
    tcUntilDate: string;
    serviceStartDate: string;
    serviceEndDate: string;
    totalsInvoiceCurrency?: InvoiceExportTotalsDto;
    totalsPaymentCurrency?: InvoiceExportTotalsDto;
    totalsBaseCurrency?: InvoiceExportTotalsDto;
    latestInvoiceTotalsPaymentCurrency?: LatestInvoicesDto;
    debtNotes: string;
    paidOnDate: string;
    issuedForCases: MinDto[];
    serviceAgreements: ContractMinDetailsDto[];
    primaryContacts: MinDto[];
    supervisingPartners: MinDto[];
    responsibleLawyers: MinDto[];
    feeArrangementTypeCodes: string[];
    practiceAreas: string[];
    invoiceSignedBy: MinDto;
    usedHours: number;
    billedHours: number;
    invoicedHours: number;
}

interface InvoicePayer {
    id: number;
    name: string;
    regNrOrIdCode: string;
    payerId: string;
    vatNumber: string;
    region: string;
    countryCode: string;
    city: string;
    street: string;
    postalCode: string;
}

interface RelatedInvoiceDto {
    id: number;
    invoiceNumber: string;
}

interface InvoiceMatterItemDto {
    id: number;
    name: string;
    caseNumber: string;
    fullName: string;
    pricelistCurrencyTypeCode?: string;
    pricingLimitationTypeCode?: string;
    capUnitTypeCode?: string;
    capLimit: number;
    fixedFee: number;
}

export interface PrepaymentDataListItem {
    id: number;
    invoiceDate: string;
    invoiceDescription: string;
    payments: InvoicePayment[];
    paymentCurrencyTypeCode: string;
    totalAmount: number;
    usedAmount: number;
    remainingAmount: number;
    usedAmountByCurrentInvoice: number;
    isConnected: boolean;
    mainMatter?: MatterRef;
}

export interface MatterRef {
    id: number;
    caseNumber: string;
    name: string;
}

export interface QrCodeDto {
    code: string;
    qrCodeType: string;
}

export interface InvoiceExternalEventDto {
    id: number;
    directionTypeCode: string;
    eventTimestamp: string;
    actionTypeCode: string;
    externalInvoiceState?: string;
    externalInvoiceStateReason?: string;
    externalInvoiceNumber?: string;
    externalId?: string;
    qrCodes?: QrCodeDto[];
}

export const ExternalStatusType = {
    IGNORE: 'IGNORE',
    NOT_SENT: 'NOT_SENT',
    PENDING: 'PENDING',
    APPROVED: 'APPROVED',
    REJECTED: 'REJECTED',
    SKIPPED: 'SKIPPED',
} as const;

export type ExternalStatusType = (typeof ExternalStatusType)[keyof typeof ExternalStatusType];

export interface InvoiceData {
    id: number;
    isClientComplianceStatusNotVerified: boolean;
    isDeletable: boolean;
    isEditable: boolean;
    invoiceDateLockedToCurrentDate: boolean;
    isTemplateEditable: boolean;
    clientParty: ContactMinDto & BillingInvoiceInfo & AuditDates;
    receiverParty?: ContactMinDto & BillingInvoiceInfo & AuditDates;
    invoiceTypeCode: string;
    invoiceAccountingTypeCode: string;
    vatCalculationTypeCode: string;
    invoiceNumber: string;
    invoiceDate: string;
    issueDate?: string;
    dueDate?: string;
    serviceDate?: string;
    paidOnDate?: string;
    maxInvoiceTimecardOrExpenseDate?: string;
    invoiceStatusTypeCode: string;
    invoicedByOffice: OfficeMin;
    invoiceTemplate?: DocumentTemplateMinDto;
    invoiceSpecTemplate?: DocumentTemplateMinDto;
    signedByEmployee: EmpMinDto;
    supervisedByEmployee: EmpMinDto;
    primaryRecipientParty?: ContactMinDto;
    parties: InvoiceParty[];
    primaryRecipientAdditionalEmails?: string;
    secondaryRecipientAdditionalEmails?: string;
    invoiceCurrencyTypeCode: string;
    paymentCurrencyTypeCode: string;
    billingInstructions?: string;
    invoiceDescription?: string;
    invoiceDescriptionLocalLanguage?: string;
    outlineDescription?: string;
    outlineDescriptionLocalLanguage?: string;
    invoiceMatters: InvoiceMatterDetailsDto[];
    createdAt: Date;
    createdBy: string;
    createdByEmployee: MinDto | null;
    lastModifiedAt: Date;
    changedByEmployee: MinDto | null;
    invoiceTransitions: InvoiceTransitionDto[];
    invoicePayments: InvoicePayment[];
    prepaymentsUsed: InvoicePayment[];
    hasUninvoicedTimecardsThisMonth: boolean;
    hasUninvoicedTimecardsPrevMonth: boolean;
    hasUninvoicedExpensesThisMonth: boolean;
    hasUninvoicedExpensesPrevMonth: boolean;
    hasMatterHourlyPriceChanged: boolean;
    hasCreditInvoice: boolean;
    creditInvoices?: InvoiceMinDto[];
    previousInvoices?: InvoiceMinDto[];
    invoiceArchiveEmails?: string;
    paymentNotificationReceivers?: {
        employee: NamedEntityMinDto;
        matterEmployeeRoleTypeCode: string;
    }[];
    debitInvoice?: { id: number; invoiceNumber: string };
    matter: {
        id: number;
        fullName: string;
        isJointInvoiceDisabled: boolean;
        responsibleEmployee?: MinDto;
    };
    isSpecAvailable: boolean;
    containsMissingJobRole: boolean;
    containsMissingHourlyRate?: boolean;
    missingHourlyRateEmployeeNames?: string[];
    paymentCurrencyConversionDate?: string;
    baseCurrencyConversionDate?: string;
    paymentCurrencyConversionRate?: string;
    baseCurrencyConversionRate?: string;
    baseCurrencyPaymentCurrencyConversionRate?: string;
    additionalAddress?: string;
    additionalInfo?: string;
    isSplit?: boolean;
    invoiceCopies?: InvoiceMinDto[];
    payers?: InvoicePayerDto[];
    paymentShareRate?: number | null;
    paymentShareAmount?: number | null;
    isSharedPayment?: boolean;
    sharedPaymentGroup?: SharedPaymentGroupDto | null;
    /** The full amounts of a shared-payment copy; `totals` holds this copy's share. */
    preSplitTotals?: InvoiceTotals | null;
    customization: CustomizationDto;
    excludeFromReporting: boolean;
    isSpecIncludedInInvoice: boolean;
    isSpecExcludedFromEmail: boolean;
    isDeltaVatRoundingEnabled: boolean;
    isDeltaVatRoundingFeasible?: boolean;
    debtNotes?: string;
    nextDebtCollectionActionTypeCode?: string;
    nextDebtCollectionActionSetAt?: Date;
    nextDebtCollectionActionSetBy?: MinDto;
    receivabilityTypeCode?: string;
    receivabilitySetAt?: Date;
    receivabilitySetBy?: MinDto;
    penaltyAmount?: number;
    interestPaidOn?: string;
    nextReminderDate?: string;
    interestRate?: number;
    serviceStartDate?: string;
    serviceEndDate?: string;
    invoiceReminders?: InvoiceReminderDto[];
    secondaryRecipientMainEmails: string[];
    isFixedFeeMattersOnly: boolean;
    downloadButtonsDisabled?: boolean;
    originatesFromInvoice?: InvoiceCorrectionDto;
    correctionInvoice?: InvoiceCorrectionDto;
    deltaAmountsJson?: InvoiceTotals;
    isRestricted?: boolean;
    restrictedAccessMessage?: string;
    externalStatusTypeCode?: string;
    isPresentInAccountingIntegration?: boolean;
    isPresentInInvoicingIntegration?: boolean;
    isInvoicingReferenceMissing?: boolean;
    accountingIntegrationInvoiceUrl?: string | null;
    referenceNumber?: string | null;
    clientReference?: string | null;
    invoiceExternalEvents?: InvoiceExternalEventDto[];
    externalPayload?: string | null;
    hasDebitInvoicedTimecardsOrExpenses?: boolean;
    isDisplayedInPaymentCurrency?: boolean;
}

interface InvoiceReminderDto {
    sentAt: string;
    debtReminderType: DebtReminderType;
}

/** A payer in the list's order: the first one is the main payer. VAT is known per payer only once the invoice is split. */
export interface InvoicePayerDto {
    id: number;
    party: MinDto;
    shareRate: number;
    shareAmount: number;
}

export interface SharedPaymentGroupDto {
    invoiceCount: number;
    position: number;
    isLocked: boolean;
    lockedByInvoiceNumber: string | null;
    /** The sum of the live copies' share rates. */
    shareTotal: number;
    /** The live copies' shares add up to 100 %; a copy cannot leave FINALIZED until they do. */
    isComplete: boolean;
}

/** The payers in their order: the first one is the main payer, which becomes the invoice's payer. */
export interface UpdateInvoicePayersDto {
    id: number;
    payers: { partyId: number; shareRate: number }[];
}

export interface PaymentDistributionInvoiceDto {
    id: number;
    invoiceNumber: string;
    payer: MinDto;
    status: InvoiceStatusType;
    shareRate: number;
    shareAmount: number;
    /** The copy's share with its payer's VAT, as its stored totals carry it; recalculated when the shares are saved. */
    payableAmount: number;
    isDeletable: boolean;
}

/** A credited copy of the group: kept out of the distribution and its totals. */
export interface PaymentDistributionCreditedInvoiceDto {
    id: number;
    invoiceNumber: string;
    payer: MinDto;
    shareRate: number;
    shareAmount: number;
}

export interface PaymentDistributionDto {
    matterName: string;
    /** Every case of the invoice, a joint invoice's included. */
    matterNames?: string[];
    currencyCode: string;
    fullTotalWithoutVat: number;
    fullTotalWithVat: number;
    isLocked: boolean;
    lockedByInvoiceNumber: string | null;
    canEdit: boolean;
    invoices: PaymentDistributionInvoiceDto[];
    creditedInvoices?: PaymentDistributionCreditedInvoiceDto[];
}

/**
 * The refusal of a payment distribution read or save on an invoice that is no longer shared: its group was left
 * with one invoice. The dialog closes on it rather than showing it.
 */
export function isNotSharedPaymentRefusal(error: unknown): boolean {
    return (error as { data?: { code?: unknown } } | undefined)?.data?.code === 'NOT_SHARED_PAYMENT_INVOICE';
}

export interface UpdatePaymentDistributionDto {
    id: number;
    shares: { invoiceId: number; shareRate: number }[];
}

export interface GroupingInvoiceRowUpdateDto {
    invoiceId: number;
    matterId: number;
    groupingRowId: string;
    sourceType: string;
    vatRate: number;
    description?: string;
    isDeleted?: boolean;
    appliedCap?: number;
    totalAmount?: number;
    totalBeforeAdjustment?: number;
    discountRate?: number;
}

export interface InvoiceTotals {
    invoiceDetailsTotals?: InvoiceDetailsTotals[];
    invoiceCurrency: InvoiceAmountDetailsTotalsDto;
    baseCurrency: InvoiceAmountDetailsTotalsDto;
    paymentCurrency: InvoiceAmountDetailsTotalsDto;
    cachedPaymentCurrencyTotalWithVat?: number;
}

export interface RoundingAdjustmentsDto {
    vat: number | null;
    totalWithVat: number | null;
    totalWithoutVat: number | null;
}

export interface UpdateRoundingAdjustmentsDto {
    invoiceCurrency: RoundingAdjustmentsDto;
    baseCurrency: RoundingAdjustmentsDto;
    paymentCurrency: RoundingAdjustmentsDto;
    isDeltaVatRoundingEnabled: boolean;
}

export interface InvoiceAmountDetailsTotalsDto {
    currencyTypeCode: string;
    fees: AmountTotalsDto;
    expensesWithVat: AmountTotalsDto;
    expensesWithoutVat: AmountTotalsDto;
    invoice: {
        vat: number;
        totalWithVat: number;
        totalWithoutVat: number;
        amountDue: number;
        unpaidAmount: number;
        prepaymentAmount: number;
    };
    vatRateAmounts: Map<string, number>;
    roundingAdjustments: RoundingAdjustmentsDto;
}

interface AmountTotalsDto {
    vat: number;
    vatRate: number;
    totalWithVat: number;
    totalWithoutVat: number;
    fixedFees: number;
    discountRate: number;
    cashAdjustment: number;
    totalBeforeAdjustments: number;
    discountAdjustmentAmount: number;
}

export interface InvoiceDetailsTotals {
    matterId: number;
    matterNumber: string;
    matterName: string;
    fullName: string;
    feeArrangementTypeCode: string;
    matterBillingSummary?: MatterBillingSummaryDto;
    invoiceTimecardTotals?: InvoiceTimecardTotals[];
    invoiceExpenseTotals?: InvoiceExpenseTotals[];
    invoiceRowTotals?: InvoiceRowTotals[];
}

interface CommonTotalsDto {
    description?: string;
    discountRate?: number;
    discountAmount?: number;
    vatRate?: number;
    vatAmount?: number;
    totalWoVat?: number;
    totalWithVat?: number;
    vatAmountPaymentCurrency?: number;
    totalWithVatPaymentCurrency?: number;
    totalWoVatPaymentCurrency?: number;
    vatAmountBaseCurrency?: number;
    totalWithVatBaseCurrency?: number;
    totalWoVatBaseCurrency?: number;
}

export interface InvoiceTimecardTotals extends CommonTotalsDto {
    groupingRowId?: string;
    feeArrangementTypeCode?: string;
    timecardGroupingTypeCode?: string;
    totalHours?: number;
    totalBilledHours?: number;
    averageHourlyPrice?: number;
    totalBeforeAdjustments?: number;
    amountBeforeDiscountInPaymentCurrency?: number;
    itemTotalInPaymentCurrency?: number;
    amountAdjustmentDiscount?: number;
    amountAdjustmentCash?: number;
    totalAfterAdjustments?: number;
    totalAfterAdjustmentsWithVat?: number;
}

export interface InvoiceExpenseTotals extends CommonTotalsDto {
    expenseGroupingTypeCode?: string;
    totalExpenses: number;
    expenseTypes: string;
    totalBeforeAdjustments?: number;
    amountBeforeDiscountInPaymentCurrency?: number;
    itemTotalInPaymentCurrency?: number;
    currencyTypeCode?: string;
    isNonVat?: boolean;
    amountAdjustmentDiscount?: number;
    amountAdjustmentCash?: number;
    totalAfterAdjustments?: number;
    totalAfterAdjustmentsWithVat?: number;
    groupingRowId?: string;
}

export interface InvoiceRowTotals extends CommonTotalsDto {
    invoiceRowId: number;
    itemTotal?: number;
    itemTotalInPaymentCurrency?: number;
    amountBeforeDiscountInPaymentCurrency?: number;
    amountBeforeDiscount?: number;
    invoiceRowTypeCode: string;
    adminFeeRate?: number;
    editIntentTypeCode: EditIntentTypeCode | null;
    initialAmountsJson?: InvoiceRowCalculationLog;
    isRemoved: boolean;
}

interface InvoiceRowCalculationLog {
    initialAmount: number;
    discountRate: number;
    discountAdjustmentAmount: number;
    finalAmount: number;
    vatRate: number;
    vat: number;
}

export interface InvoiceMinDto {
    id: number;
    invoiceTypeCode: string;
    invoiceNumber: string;
    invoiceDate: string;
    dueDate: string;
    invoiceStatusTypeCode: string;
    paidOnDate: string | null;
    debitInvoiceId?: number;
    clientParty: MinDto;
    outstandingBalanceInPaymentCurrency: string;
    paymentCurrencyTypeCode: string;
    totalAmountInPaymentCurrency: string;
    invoicedByOffice: OfficeMin;
    hasCreditInvoice: boolean;
    invoicePdfLanguage?: string;
}

export interface InvoiceCorrectionDto {
    id: number;
    invoiceDate: string;
    invoiceNumber: string;
    invoiceTypeCode: string;
    invoiceStatusTypeCode: string;
    originatesFromInvoiceId: number;
    totalCorrectionAmountPaymentCurrency: number;
    correctionInvoice?: InvoiceCorrectionDto;
}

export interface InvoiceReminderIntervalDto {
    debtReminderType: string;
    isPenaltyCalculationTriggered: boolean;
}

export interface InvoiceDebtReminderDto extends InvoiceMinDto {
    isSpecAvailable: boolean;
    primaryRecipientParty?: NamedEntityMinWithMainEmailDto;
    primaryRecipientAdditionalEmails?: string;
    parties: InvoiceParty[];
    secondaryRecipientAdditionalEmails?: string;
    invoicedByOffice: OfficeMin;
    debtCollectionReminderIntervals: InvoiceReminderIntervalDto[];
    invoiceArchiveEmails?: string;
}

export interface TimecardExportInvoiceDto {
    id: number;
    invoiceNumber: string;
    feesAfterAdjustments: number;
}

interface InvoiceExportTotalsDto {
    feesBeforeAdjustments: number;
    feesAfterAdjustmentsBeforeVat: number;
    vatOnFees: number;
    vatOnExpenses: number;
    discountAdjustments: number;
    otherAdjustments: number;
    expenses: number;
    nonVatExpenses: number;
    totalWithoutVat: number;
    totalVat: number;
    totalAmount: number;
    prepayment: number;
    prepaymentWoVat: number;
    payableAmount: number;
    remainsUnpaid: number;
}

interface LatestInvoicesDto {
    totalVat: number;
    totalAmount: number;
}

export interface ExistingPrepaymentDto {
    id: number;
    remainingAmount: number;
    isConnected: boolean;
    payments: InvoicePayment[];
}

export interface ExistingPrepaymentListForm {
    existingPrepayments: ExistingPrepaymentForm[];
}

export interface ExistingPrepaymentForm {
    id: number;
    invoiceDate: string;
    invoiceDescription: string;
    payments: InvoicePayment[];
    totalAmount: number;
    usedAmount: number;
    remainingAmount: number;
    usedAmountByCurrentInvoice: number;
    isConnected: boolean;
    mainMatter?: MatterRef;
}

export interface InvoiceTransitionDto {
    id: number;
    invoiceId: number;
    fromInvoiceStatusType: InvoiceStatusType;
    toInvoiceStatusType: InvoiceStatusType;
    invoiceTransitionActionType: InvoiceTransactionActionType;
    changedByEmployee: MinDto;
    comment?: string;
    matter?: MinDto;
    createdAt: Date;
}

export enum InvoicePartyRoleTypeCode {
    SECONDARY_RECIPIENT = 'SECONDARY_RECIPIENT',
}
interface InvoiceParty {
    party: ContactMinDto;
    invoicePartyRoleTypeCode: InvoicePartyRoleTypeCode;
}

export interface InvoiceForm {
    isPaymentCurrencyDifferent: boolean;
    paymentCurrencyType: { label: string; value: string } | null;
    invoiceAccountingType: { label: string; value: string } | null;
    vatCalculationType: { label: string; value: string } | null;
    invoicedByOffice: NamedEntityMinDto | null;
    invoiceTemplate: NamedEntityMinDto | null;
    invoiceSpecTemplate: NamedEntityMinDto | null;
    signedByEmployee: NamedEntityMinDto | null;
    supervisedByEmployee: NamedEntityMinDto;
    clientParty: ContactMinDto & BillingInvoiceInfo;
    receiverParty?: ContactMinDto & BillingInvoiceInfo;
    billingInstructions: string | null;
    invoiceDate: string | null;
    issueDate: string | null;
    dueDate: string | null;
    serviceDate: string | null;
    maxInvoiceTimecardOrExpenseDate: string | null;
    invoiceCurrencyType: { label: string; value: string } | null;
    invoiceNumber: string | null;
    invoiceDescription: string | null;
    invoiceDescriptionLocalLanguage: string | null;
    outlineDescription: string | null;
    outlineDescriptionLocalLanguage: string | null;
    primaryRecipientParty: ContactMinDto | null;
    primaryRecipientAdditionalEmails: string | null;
    secondaryRecipientParties: ContactMinDto[];
    secondaryRecipientAdditionalEmails: string | null;
    invoiceTransitions: InvoiceTransitionDto[];
    paymentCurrencyConversionDate: string | null;
    baseCurrencyConversionDate: string | null;
    additionalAddress: string | null;
    additionalInfo: string | null;
    excludeFromReporting: boolean;
    isSpecIncludedInInvoice: boolean;
    isSpecExcludedFromEmail: boolean;
    nextDebtCollectionActionType: { label: string; value: string } | null;
    receivabilityType: { label: string; value: string } | null;
    penaltyAmount: string | null;
    interestPaidOn: string | null;
    nextReminderDate: string | null;
    serviceStartDate: string | null;
    serviceEndDate: string | null;
    paymentNotificationReceivers: NamedEntityMinDto[];
    debtNotes: string | null;
    interestRate: string | null;
    downloadButtonsDisabled?: boolean;
    isDisplayedInPaymentCurrency: boolean;
    invoiceDateManuallyUpdated: boolean | null;
    issueDateManuallyUpdated: boolean | null;
    dueDateManuallyUpdated: boolean | null;
}

export interface InvoiceTotalsForm {
    invoiceDetailsTotals?: InvoiceDetailsTotalsFormDto[];
}

export interface InvoiceDetailsTotalsFormDto {
    matterId: number;
    matterNumber: string;
    matterName: string;
    fullName: string;
    invoiceTimecardTotals?: InvoiceTotalsFormDto[];
    invoiceExpenseTotals?: InvoiceTotalsFormDto[];
    invoiceRowTotals?: InvoiceRowTotalsFormDto[];
    adminFeeRowTotals?: InvoiceRowTotalsFormDto[];
}

export interface InvoiceTotalsFormDto {
    description?: string;
    discountRate?: number;
    totalWoVat?: number;
    vatRate?: number;
    totalWithVat?: number;
}

export interface InvoiceRowTotalsFormDto extends InvoiceTotalsFormDto {
    id: number;
    selected: boolean;
    editIntentTypeCode: EditIntentTypeCode | null;
}

export interface InvoiceRowCorrectionForm {
    rows: InvoiceRowCorrectionFormRow[];
}

export interface InvoiceRowCorrectionFormRow {
    id: number;
    selected: boolean;
    editIntentTypeCode: EditIntentTypeCode | null;
}

export interface InvoiceRowBulkUpdateDto {
    id: number;
    editIntentTypeCode: EditIntentTypeCode | null;
}

export interface UpdateInvoiceLimitedDto {
    id: number;
    billingInstructions: string | null;
    primaryRecipientParty: NumericIdPrimKeyDto | null;
    primaryRecipientAdditionalEmails: string | null;
    secondaryRecipientParties: NumericIdPrimKeyDto[];
    secondaryRecipientAdditionalEmails: string | null;
    excludeFromReporting: boolean;
}

export interface UpdateInvoiceTemplateDto {
    id: number;
    invoiceTemplate: NumericIdPrimKeyDto | null;
    invoiceSpecTemplate: NumericIdPrimKeyDto | null;
}

export interface InvoiceUpdateDto {
    id: number;
    clientParty: NamedEntityMinDto;
    receiverParty: NamedEntityMinDto | null;
    paymentCurrencyTypeCode: string | null;
    invoicedByOffice: NamedEntityMinDto | null;
    invoiceAccountingTypeCode: string | null;
    vatCalculationTypeCode: string | null;
    invoiceTemplate: number | null;
    invoiceSpecTemplate: number | null;
    signedByEmployee: NamedEntityMinDto | null;
    billingInstructions: string | null;
    invoiceDate: string | null;
    issueDate: string | null;
    dueDate: string | null;
    serviceDate: string | null;
    invoiceNumber: string | null;
    invoiceDescription: string | null;
    invoiceDescriptionLocalLanguage: string | null;
    outlineDescription: string | null;
    outlineDescriptionLocalLanguage: string | null;
    primaryRecipientParty: NumericIdPrimKeyDto | null;
    primaryRecipientAdditionalEmails: string | null;
    secondaryRecipientParties: NumericIdPrimKeyDto[];
    secondaryRecipientAdditionalEmails: string | null;
    paymentCurrencyConversionDate: string | null;
    baseCurrencyConversionDate: string | null;
    additionalAddress: string | null;
    additionalInfo: string | null;
    excludeFromReporting: boolean;
    isSpecIncludedInInvoice: boolean;
    isSpecExcludedFromEmail: boolean;
    serviceStartDate: string | null;
    serviceEndDate: string | null;
    paymentNotificationReceivers: NamedEntityMinDto[];
    downloadButtonsDisabled?: boolean;
    isDisplayedInPaymentCurrency?: boolean;
    invoiceDateManuallyUpdated: boolean | null;
    issueDateManuallyUpdated: boolean | null;
    dueDateManuallyUpdated: boolean | null;
}

export interface InvoicePaymentFormDto {
    invoiceId: number | null;
    date: string | null;
    amount: string | null;
    paymentNote: string | null;
}

export interface InvoicePayment {
    id: number;
    invoicePaymentTypeCode: string;
    date: string;
    amount: number;
    amountInTransactionCurrency?: string;
    paymentNote?: string;
    currencyTypeCode: string;
    transactionCurrencyTypeCode?: string;
    transactionCurrencyConversionDate?: string;
    transactionCurrencyConversionRate?: string;
    bankTransaction?: {
        id: number;
        entryTransactionId: string;
    };
    isPartOfInvoice?: boolean;
    invoice: InvoiceMinDto;
    prepaidInvoice?: InvoiceMinDto;
    correctionInvoice?: InvoiceMinDto;
}

export interface InvoiceRowCreateDto {
    invoiceId: number;
    matterId: number;
    description: string;
    totalBeforeAdjustment?: number;
    totalAmount: number;
    discountRate: number;
    invoiceRowTypeCode?: string;
    adminFeeRate?: number;
}

export interface InvoiceRowUpdateDto {
    id: number;
    description: string;
    totalBeforeAdjustment?: number;
    totalAmount: number;
    discountRate: number;
    invoiceId: number;
    adminFeeRate?: number;
}

export interface InvoiceEmailFormBase {
    invoicedByOffice?: NamedEntityMinWithEmailDto;
    to: string[];
    cc: string[];
    bcc: string[];
    replyTo: string[];
    subject: string;
    content: string;
    customEmailTemplateLanguage?: { label: string; value: string } | null;
}

export interface InvoiceEmailForm extends InvoiceEmailFormBase {
    signature: string;
    files: File[] | null;
    generatedAttachments: string[];
}

export interface InvoiceDebtReminderEmailForm extends InvoiceEmailFormBase {
    // signature: string;
    selectedInvoices: number[];
    includePdfs: boolean;
    includePenaltyPdfs?: boolean;
    additionalDebtReminderText?: string;
}

export type EmailFields = keyof Pick<InvoiceEmailFormBase, 'to' | 'cc' | 'bcc' | 'replyTo'>;

export interface SendableInvoice {
    id: number;
    invoiceNumber: string;
    invoiceTypeCode: string;
    invoiceStatusTypeCode: string;
    invoicedByOffice: OfficeMin;
    isFixedFeeMattersOnly: boolean;
    isSpecAvailable: boolean;
    isSpecIncludedInInvoice: boolean;
    isSpecExcludedFromEmail: boolean;
    primaryRecipientAdditionalEmails?: string;
    primaryRecipientParty?: ContactMinDto;
    secondaryRecipientMainEmails: string[];
    secondaryRecipientAdditionalEmails?: string;
    invoiceArchiveEmails?: string;
    invoicePdfLanguage?: string;
}

export interface GroupingInvoiceRowEditForm {
    description?: string;
    totalAmount?: number;
    amountBeforeDiscount?: number;
    isDeleted?: boolean;
    discountRate?: number;
    discountAmount?: string;
    vatRate: number;
    groupingRowId: string;
    sourceType: string;
    matterId: number;
}

export interface InvoiceRowForm {
    invoiceRowId: number;
    matterId: number;
    description: string;
    itemTotal?: number;
    amountBeforeDiscount?: number;
    discountRate?: number;
    discountAmount?: string;
    invoiceRowTypeCode?: string;
    adminFeeRate?: number;
}

export interface InvoiceListItemTotalsDto {
    invoiceCurrency?: InvoiceAmountDetailsTotalsDto;
    baseCurrency?: InvoiceAmountDetailsTotalsDto;
    paymentCurrency?: InvoiceAmountDetailsTotalsDto;
}

export interface InvoiceMatterDetailsDto {
    matterId: number;
    matterNumber: string;
    matterName: string;
    fullName: string;
    isConfidential?: boolean | null;
    responsibleEmployee?: NamedEntityMinDto;
    supervisedByEmployee: NamedEntityMinDto;
    feeArrangementTypeCode: string;
    hasMatterCapError: boolean;
    hasUnsignedServiceAgreement: boolean;
    hasAdminFeeAllowed: boolean;
    adminFeePercentage?: number;
    adminFeeBaseAmount?: number;
    adminFeeBaseAmountInPaymentCurrency?: number;
    matterBillingDetails: BillingInvoiceInfo & AuditDates;
    serviceAgreementBillingDetails: BillingInvoiceInfo & AuditDates;
    contractId?: number;
    contractName?: string;
    invoiceMatterName?: string;
    invoiceMatterNameLocalLanguage?: string;
    specificationInvoiceMatterName?: string;
    specificationInvoiceMatterNameLocalLanguage?: string;
    clientParty: ContactMinDto & BillingInvoiceInfo & AuditDates;
    approvedAt?: string;
    approvedByEmployee?: NamedEntityMinDto;
    reviewedAt?: string;
    reviewedByEmployee?: NamedEntityMinDto;
    fixedFee?: number;
    pricelistCurrencyTypeCode?: string;
    matterClientReference?: string;
    calculationLog?: InvoiceMatterCalculationLog;
    /** Null when the firm does not close cases on send, or the case is not In progress. */
    closeOnSend: CloseOnSendDto | null;
}

export interface InvoiceInfoDto {
    invoiceDate: string;
    invoiceStatusTypeCode: string;
}

export interface InvoicesTotalsDto {
    baseCurrencyTypeCode: string;
    totalFeesBaseCurrency: number;
    totalExpensesBaseCurrency: number;
    totalAmountBaseCurrency: number;
    totalFinalPayableBaseCurrency: number;
    totalOutstandingBalanceBaseCurrency: number;
    totalFeesWithoutDiscountBaseCurrency: number;
    totalVatExpensesWithoutDiscountBaseCurrency: number;
    totalNonVatExpensesWithoutDiscountBaseCurrency: number;
    totalGrossTotalBaseCurrency: number;
    totalPrepaymentBaseCurrency: number;
    totalVatBaseCurrency: number;
    feesBreakdown: SimpleCurrencyDto[];
    expensesBreakdown: SimpleCurrencyDto[];
    totalBreakdown: SimpleCurrencyDto[];
    finalPayableBreakdown: SimpleCurrencyDto[];
    outstandingBalanceBreakdown: SimpleCurrencyDto[];
    limitExceeded: boolean;
}

export interface InvoiceBillingNotesDto {
    invoiceId: number;
    partyId?: number;
    matterId?: number;
    billingNotes?: string;
}

export interface InvoiceDebtNotesDto {
    invoiceId: number;
    debtNotes: string;
    nextDebtCollectionActionTypeCode: string | null;
    receivabilityTypeCode: string | null;
    penaltyAmount: number | null;
    interestPaidOn: string | null;
    nextReminderDate: string | null;
    interestRate: number | null;
}

export interface BulkDebtNotesUpdateDto {
    invoiceIds: number[];
    debtNotes: string | null;
    nextReminderDate?: string | null;
}

export interface CustomInvoiceForm {
    isFeeDateDisplayed: boolean;
    isServicePeriodDisplayed: boolean;
    isFeeDoneByDisplayed: boolean;
    isFeePositionDisplayed: boolean;
    isFeeHourlyRateDisplayed: boolean;
    isCoseDisplayed: boolean;
    isFeeDescriptionDisplayed: boolean;
    isFeeUsedHoursDisplayed: boolean;
    isFeeBilledHoursDisplayed: boolean;
    isFeeGrandTotalDisplayed: boolean;
    isFeeAmountDisplayed: boolean;
    isFeeSummedAmountDisplayed: boolean;
    isFeeSummedBilledHoursDisplayed: boolean;
    isExpenseDateDisplayed: boolean;
    isExpenseAmountDisplayed: boolean;
    isSummedExpenseAmountDisplayed: boolean;
    isExpenseTypeDisplayed: boolean;
    isExpenseDetailsDisplayed: boolean;
    isExpenseGrandTotalDisplayed: boolean;
    isPayerPrimaryRecipientDisplayed: boolean;
    isRegOrPersonalCodeDisplayed: boolean;
    isSignedByDisplayed: boolean;
    isSignedByPositionDisplayed: boolean;
    isExchangeRateDisplayed: boolean;
    isTotalsGrandDisplayed: boolean;
    isExpenseOneLineDisplayed: boolean;
    isExpenseSummaryDisplayed: boolean;
    isAssignmentSummaryDisplayed: boolean;
    isTotalsAggregatedByMatterDisplayed: boolean;
    isTotalsLegalFeesAndExpensesDisplayed: boolean;
    isAssignmentSummaryWithoutLawyerNamesDisplayed: boolean;
    isAssignmentSummaryWithoutAssignmentNamesDisplayed: boolean;
    isSortedByLawyerName: boolean;
    isOutlineDisplayed: boolean;
    isBreakdownDisplayed: boolean;
    isAdjustmentsConsideredDiscounts: boolean;
    isSpecFeeHourlyRateDisplayed: boolean;
    isCaseSupervisorDisplayed: boolean;
    isDiscountDisplayed: boolean;
    isVatLegalReferenceHidden: boolean;
    isPrimaryRecipientHidden: boolean;
    isBankAddressDisplayed: boolean;
    isDiscountRowHidden: boolean;
    isInvoiceDateDisplayed: boolean;
    isBuyerNumberDisplayed: boolean;
}

export interface UpdateCustomizationDto {
    isFeeDateDisplayed: boolean;
    isServicePeriodDisplayed: boolean;
    isFeeDoneByDisplayed: boolean;
    isFeePositionDisplayed: boolean;
    isFeeHourlyRateDisplayed: boolean;
    isCoseDisplayed: boolean;
    isFeeDescriptionDisplayed: boolean;
    isFeeUsedHoursDisplayed: boolean;
    isFeeBilledHoursDisplayed: boolean;
    isFeeGrandTotalDisplayed: boolean;
    isFeeAmountDisplayed: boolean;
    isFeeSummedAmountDisplayed: boolean;
    isFeeSummedBilledHoursDisplayed: boolean;
    isExpenseDateDisplayed: boolean;
    isExpenseAmountDisplayed: boolean;
    isSummedExpenseAmountDisplayed: boolean;
    isExpenseTypeDisplayed: boolean;
    isExpenseDetailsDisplayed: boolean;
    isExpenseGrandTotalDisplayed: boolean;
    isPayerPrimaryRecipientDisplayed: boolean;
    isRegOrPersonalCodeDisplayed: boolean;
    isSignedByDisplayed: boolean;
    isSignedByPositionDisplayed: boolean;
    isExchangeRateDisplayed: boolean;
    isTotalsGrandDisplayed: boolean;
    isExpenseOneLineDisplayed: boolean;
    isExpenseSummaryDisplayed: boolean;
    isAssignmentSummaryDisplayed: boolean;
    isTotalsAggregatedByMatterDisplayed: boolean;
    isTotalsLegalFeesAndExpensesDisplayed: boolean;
    isAssignmentSummaryWithoutLawyerNamesDisplayed: boolean;
    isAssignmentSummaryWithoutAssignmentNamesDisplayed: boolean;
    isSortedByLawyerName: boolean;
    isOutlineDisplayed: boolean;
    isBreakdownDisplayed: boolean;
    isAdjustmentsConsideredDiscounts: boolean;
    isSpecFeeHourlyRateDisplayed: boolean;
    isCaseSupervisorDisplayed: boolean;
    isDiscountDisplayed: boolean;
    isVatLegalReferenceHidden: boolean;
    isPrimaryRecipientHidden: boolean;
    isBankAddressDisplayed: boolean;
    isDiscountRowHidden: boolean;
    isInvoiceDateDisplayed: boolean;
    isBuyerNumberDisplayed: boolean;
}

export interface CustomizationDto {
    isFeeDateDisplayed: boolean;
    isServicePeriodDisplayed: boolean;
    isFeeDoneByDisplayed: boolean;
    isFeePositionDisplayed: boolean;
    isFeeHourlyRateDisplayed: boolean;
    isCoseDisplayed: boolean;
    isFeeDescriptionDisplayed: boolean;
    isFeeUsedHoursDisplayed: boolean;
    isFeeBilledHoursDisplayed: boolean;
    isFeeGrandTotalDisplayed: boolean;
    isFeeAmountDisplayed: boolean;
    isFeeSummedAmountDisplayed: boolean;
    isFeeSummedBilledHoursDisplayed: boolean;
    isExpenseDateDisplayed: boolean;
    isExpenseAmountDisplayed: boolean;
    isSummedExpenseAmountDisplayed: boolean;
    isExpenseTypeDisplayed: boolean;
    isExpenseDetailsDisplayed: boolean;
    isExpenseGrandTotalDisplayed: boolean;
    isPayerPrimaryRecipientDisplayed: boolean;
    isRegOrPersonalCodeDisplayed: boolean;
    isSignedByDisplayed: boolean;
    isSignedByPositionDisplayed: boolean;
    isExchangeRateDisplayed: boolean;
    isTotalsGrandDisplayed: boolean;
    isExpenseOneLineDisplayed: boolean;
    isExpenseSummaryDisplayed: boolean;
    isAssignmentSummaryDisplayed: boolean;
    isTotalsAggregatedByMatterDisplayed: boolean;
    isTotalsLegalFeesAndExpensesDisplayed: boolean;
    isAssignmentSummaryWithoutLawyerNamesDisplayed: boolean;
    isAssignmentSummaryWithoutAssignmentNamesDisplayed: boolean;
    isSortedByLawyerName: boolean;
    isOutlineDisplayed: boolean;
    isBreakdownDisplayed: boolean;
    isAdjustmentsConsideredDiscounts: boolean;
    isSpecFeeHourlyRateDisplayed: boolean;
    isCaseSupervisorDisplayed: boolean;
    isDiscountDisplayed: boolean;
    isVatLegalReferenceHidden: boolean;
    isPrimaryRecipientHidden: boolean;
    isBankAddressDisplayed: boolean;
    isDiscountRowHidden: boolean;
    isInvoiceDateDisplayed: boolean;
    isBuyerNumberDisplayed: boolean;
}

export interface InvoiceSendValidationDto {
    invoiceId: number;
    hasInvoiceDateWarning: boolean;
    proposedInvoiceDate: string;
    hasDueDateWarning: boolean;
    proposedDueDate: string;
    paymentDeadlineInDays: number;
    hasUnusedPrepayment: boolean;
    hasLinkedPrepaymentPaidAfterInvoiceDate: boolean;
}
